import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { notification } from '@spark/ui-components/utils/notification';
import {
  createFilialOuvidoria,
  getFilialOuvidoriaById,
  updateFilialOuvidoria,
} from '@spark/services';
import { formSections, formStructure, validateForm } from './formConfig';

const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [initialData, setInitialData] = useState(null);

  const isEditMode = !!id && id !== 'criar' && !isReadOnly;

  useEffect(() => {
    if (!(isReadOnly || isEditMode) || !id || id === 'criar') {
      return;
    }

    const load = async () => {
      try {
        setLoadingData(true);
        const data = await getFilialOuvidoriaById(id);
        setInitialData({
          id: data?.id,
          nomeFilial: data?.nomeFilial || '',
        });
      } catch (error) {
        console.error('Erro ao carregar filial da ouvidoria:', error);
        notification.error('Nao foi possivel carregar a filial da ouvidoria.');
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, [id, isEditMode, isReadOnly]);

  const defaultValues = useMemo(
    () => ({
      nomeFilial: '',
    }),
    []
  );

  const initialValues = initialData || defaultValues;

  const buildComparablePayload = useCallback(values => ({
    nomeFilial: values?.nomeFilial?.trim() || '',
  }), []);

  const hasChanges = useCallback(
    values => {
      if (!isEditMode || !initialData) {
        return true;
      }

      const current = buildComparablePayload(values);
      const original = buildComparablePayload(initialData);
      return JSON.stringify(current) !== JSON.stringify(original);
    },
    [buildComparablePayload, initialData, isEditMode]
  );

  const onSubmit = useCallback(
    async values => {
      if (isEditMode && !hasChanges(values)) {
        notification.warning('Nenhuma informacao alterada');
        return;
      }

      try {
        setLoading(true);

        const payload = {
          nomeFilial: values.nomeFilial?.trim() || '',
        };

        if (isEditMode) {
          await updateFilialOuvidoria(id, payload);
          notification.success('Filial da ouvidoria atualizada com sucesso!');
        } else {
          await createFilialOuvidoria(payload);
          notification.success('Filial da ouvidoria criada com sucesso!');
        }

        history.push('/pages/filiaisouvidoria');
      } catch (error) {
        console.error('Erro ao salvar filial da ouvidoria:', error);
        notification.error('Erro ao salvar a filial da ouvidoria. Tente novamente.');
      } finally {
        setLoading(false);
      }
    },
    [hasChanges, history, id, isEditMode]
  );

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: 50 }}>Carregando dados da filial da ouvidoria...</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues} validate={validateForm}>
      {({ handleSubmit }) => (
        <Card>
          <CardBody>
            <FormContainer onSubmit={handleSubmit}>
              <DynamicForm
                structure={formStructure}
                sections={formSections(initialValues)}
                isViewMode={isReadOnly}
                isEditing={isEditMode}
              />

              {!isReadOnly && (
                <FormButtonToolbar className="mt-5">
                  <ButtonWithLoading
                    variant="primary"
                    type="submit"
                    loading={loading ? 'true' : undefined}
                    disabled={loading}
                  >
                    {isEditMode ? 'Atualizar' : 'Salvar'}
                  </ButtonWithLoading>

                  <Button variant="secondary" type="button" onClick={() => history.goBack()}>
                    Cancelar
                  </Button>
                </FormButtonToolbar>
              )}
            </FormContainer>
          </CardBody>
        </Card>
      )}
    </Form>
  );
};

export default Forms;
